import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { getRentalBooking, depositCheckout, zar } from "@/api";

export default function PayDeposit() {
  const { reference } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getRentalBooking(reference)
      .then(setBooking)
      .catch(() => setError("This booking reference wasn't found. Check the link in your email or call 010 534 9926."));
  }, [reference]);

  const pay = async () => {
    setLoading(true);
    try {
      const res = await depositCheckout(booking.reference, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start online payment — you can also pay by EFT.");
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div data-testid="pay-deposit-error" className="min-h-screen flex items-center justify-center px-4 pt-20 text-center">
        <div>
          <AlertTriangle size={48} className="text-amber-400 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-white mb-3">Booking not found</h1>
          <p className="text-slate-400 text-sm mb-8">{error}</p>
          <Link to="/contact" className="text-sky-300 hover:text-white font-bold text-sm">Contact us</Link>
        </div>
      </div>
    );
  }

  if (!booking) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="pay-deposit-loading">Loading booking…</div>;
  }

  if (booking.deposit_paid) {
    return (
      <div data-testid="pay-deposit-paid" className="min-h-screen flex items-center justify-center px-4 pt-20 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
          <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-8" />
          <h1 className="font-display text-4xl font-black text-white mb-4">Already paid.</h1>
          <p className="text-slate-400 text-sm">
            The deposit for booking <strong className="text-[#F57C00]">{booking.reference}</strong> is confirmed. See you at collection!
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="pay-deposit-page" className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">CompCar Motors</p>
        <h1 className="font-display text-3xl font-black text-white mb-2">Secure your booking.</h1>
        <p className="text-sm text-slate-400 mb-8">
          Hi {booking.first_name} — pay the refundable security deposit for booking{" "}
          <strong className="text-[#F57C00]">{booking.reference}</strong>.
        </p>

        <div className="bg-[#0B1F4E] rounded-lg p-5 space-y-3 text-sm mb-8" data-testid="pay-deposit-summary">
          <div className="flex justify-between"><span className="text-slate-500">Vehicle</span><span className="text-white font-semibold">{booking.vehicle_name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Dates</span><span className="text-white">{booking.start_date} → {booking.end_date}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Duration</span><span className="text-white">{booking.days} day{booking.days !== 1 ? "s" : ""}</span></div>
          {booking.estimated_total > 0 && (
            <div className="flex justify-between"><span className="text-slate-500">Estimated rental</span><span className="text-white">{zar(booking.estimated_total)}</span></div>
          )}
          <div className="flex justify-between border-t border-sky-300/20 pt-3">
            <span className="text-sky-300 font-bold uppercase tracking-wider text-xs">Deposit due now</span>
            <span className="font-display text-xl font-black text-[#F57C00]" data-testid="pay-deposit-amount">R 3 000</span>
          </div>
        </div>

        <button
          data-testid="pay-deposit-pay-btn"
          onClick={pay}
          disabled={loading}
          className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2 mb-4"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
          {loading ? "Opening secure checkout…" : "Pay deposit securely"}
        </button>
        <p className="text-[11px] text-slate-500 leading-relaxed text-center">
          Card payment via Stripe's secure checkout. Prefer EFT or debit order?
          Call 010 534 9926 and we'll arrange it. The deposit is refundable per your rental terms.
        </p>
      </motion.div>
    </div>
  );
}
